import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import { LinearProgress } from '@mui/material';
import cx from 'classnames';

import useDebounce from '../../../helpers/useDebounce';
import RoleSelector from '../../modules/RoleSelector';
import { RolesLowerCase, USER } from '../../../enums';
import { getUsersByRolesArray } from '../../../api';
import styles from './CreateUserModal.module.scss';
import { isEmail } from '../../../helpers';

type TUserInputField = {
  removeUser: (num: number) => void;
  allRoles: { title: string; id: string }[];
  onUserSet: (
    data: { username: string; email: string; role_id: string; temp_id: number; allow_download_own_composers?: boolean },
    index: number,
  ) => void;
  errors: Record<string, string>;
  index: number;
  tempId: number;
};

const UserInputField: React.FC<TUserInputField> = ({ index, errors, allRoles, removeUser, onUserSet, tempId }) => {
  const [value, setValue] = useState({ username: '', email: '', role_id: '', role: '', allow_download_own_composers: true });
  const [loading, setLoading] = useState(false);
  const [disableInput, setDisableInput] = useState(false);
  const notification = useSnackbar();

  const onEmailChange = (val: string) => {
    setValue({ ...value, email: val });
  };
  const onUserNameChange = (val: string) => {
    setValue((prev) => ({ ...prev, username: val }));
  };

  const selectRole = (role: string) => {
    const roleObject = allRoles.find((r) => r.title === role);
    if (roleObject?.id) {
      setValue((prev) => {
        const nextAllowDownloadOwnComposers =
          roleObject.title === RolesLowerCase.VIEWER
            ? false
            : roleObject.title === RolesLowerCase.ANALYST
            ? true
            : prev.allow_download_own_composers;

        return {
          ...prev,
          role_id: roleObject.id,
          role: roleObject.title,
          allow_download_own_composers: nextAllowDownloadOwnComposers,
        };
      });
    }
  };

  const canToggleOwnDownloads = [RolesLowerCase.VIEWER, RolesLowerCase.ANALYST].includes(value.role as RolesLowerCase);

  const debounce = useDebounce(value.email, 500);
  const fetchUser = async () => {
    if (value.email.length && isEmail(value.email)) {
      try {
        setLoading(true);
        const res = await getUsersByRolesArray(
          [RolesLowerCase.ORGANIZATION_ADMIN, RolesLowerCase.ANALYST, RolesLowerCase.VIEWER],
          value.email,
          [],
          1,
          9000,
        );
        const user = res.documents.find((u: USER) => u.email === value.email);
        if (user) {
          notification.showMessage(`A user with this email, ${value.email}, already exists`);
          if (user?.username) {
            setDisableInput(true);
            onUserNameChange(user.username);
          }
          if (user?.role?.title) {
            selectRole(user.role.title);
          }
        } else {
          setDisableInput(false);
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
  };
  useEffect(() => {
    setDisableInput(false);
    fetchUser();
  }, [debounce]);

  useEffect(() => {
    onUserSet({ ...value, temp_id: tempId }, index);
  }, [value]);

  return (
    <div key={index}>
      <div className={styles.customPadding}>
        <div className={styles.fieldContainer}>
          <TextField
            size="small"
            id="filled-basic"
            placeholder="Email"
            variant="filled"
            required
            type="email"
            sx={{
              width: '309px',
              background: '#0a1929',
              borderRadius: 'var(--ui-border-radius)',
              color: 'white',
              '& input': {
                color: 'white',
                padding: '10px',
              },
              border: errors[value.email] ? '1px solid #FF2323' : '1px solid rgba(255, 255, 255, 0.1)',
              '&:focus-within': {
                borderColor: '#39b3ea',
                boxShadow: '0 0 0 3px rgba(57, 179, 234, 0.15)',
              },
            }}
            value={value.email}
            onChange={(e) => onEmailChange(e.target.value)}
            InputProps={{ disableUnderline: true }}
          />
          {loading && <LinearProgress />}
        </div>
        {errors[value.email] && <div className={styles.errorMessage}>{errors[value.email]}</div>}
        {disableInput && <div className={styles.errorMessage}>User already exist in this organisation</div>}

        <div className={styles.fieldContainer}>
          <TextField
            size="small"
            id="filled-basic"
            disabled={disableInput}
            variant="filled"
            placeholder="Username"
            required
            sx={{
              width: '309px',
              background: '#0a1929',
              borderRadius: 'var(--ui-border-radius)',
              color: 'white',
              '& input': {
                color: 'white',
                padding: '10px',
              },
              '& .Mui-disabled': {
                color: 'white',
                '-webkit-text-fill-color': 'white !important',
              },
              border: errors[value.username] ? '1px solid #FF2323' : '1px solid rgba(255, 255, 255, 0.1)',
              '&:focus-within': {
                borderColor: '#39b3ea',
                boxShadow: '0 0 0 3px rgba(57, 179, 234, 0.15)',
              },
            }}
            value={value.username}
            onChange={(e) => onUserNameChange(e.target.value)}
            InputProps={{ disableUnderline: true }}
          />
          <CloseIcon onClick={() => removeUser(tempId)} className={styles.deleteIcon} />
        </div>
        {errors[value.username] && <div className={styles.errorMessage}>{errors[value.username]}</div>}

        <div className={cx('mb-5')}>
          <RoleSelector defaultValue={value.role} disable={disableInput} onSelect={(role) => selectRole(role)} index={index} />
        </div>
        {errors[value.role_id] && <div className={styles.errorMessage}>{errors[value.role_id]}</div>}

        {canToggleOwnDownloads && (
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={value.allow_download_own_composers}
              onChange={(e) => setValue((prev) => ({ ...prev, allow_download_own_composers: e.target.checked }))}
            />
            <span>Allow to Download their own online composers</span>
          </label>
        )}
      </div>
    </div>
  );
};

export default UserInputField;
